import { isNil } from "@clipboard-health/util-ts";
import { useEffect, useRef } from "react";

export interface BooleanStateTiming {
  startTime: number;
  endTime: number | undefined;
  duration: number | undefined;
}

export interface StateTransitionTimings {
  shift: BooleanStateTiming;
  bookability: BooleanStateTiming;
  offer: BooleanStateTiming;
  blockingRequirements: BooleanStateTiming;
  hoursRestrictionConflict: BooleanStateTiming;
  shiftBoosts: BooleanStateTiming;
  arrivalTimePoints: BooleanStateTiming;
  routeEstimate: BooleanStateTiming;
  doubleBookedShift: BooleanStateTiming;
  totalLoading: BooleanStateTiming;
}

function updateStateTiming(timing: BooleanStateTiming, isLoading: boolean): BooleanStateTiming {
  if (!isLoading && isNil(timing.endTime)) {
    const endTime = Date.now();
    return {
      ...timing,
      endTime,
      duration: endTime - timing.startTime,
    };
  }

  return timing;
}

function useTimingTracker(
  key: keyof StateTransitionTimings,
  isLoading: boolean,
  timingsRef: React.MutableRefObject<StateTransitionTimings>
): void {
  useEffect(() => {
    const timings = timingsRef.current;
    timings[key] = updateStateTiming(timings[key], isLoading);
  }, [isLoading, key, timingsRef]);
}

interface LoadingStates {
  shiftIsLoading: boolean;
  bookabilityIsLoading: boolean;
  offerIsLoading: boolean;
  isLoadingBlockingRequirements: boolean;
  hasHoursRestrictionConflictIsLoading: boolean;
  shiftBoostsAreLoading: boolean;
  isLoadingArrivalTimePoints: boolean;
  isRouteEstimateLoadingAndEnabled: boolean;
  isLoadingDoubleBookedShift: boolean;
  totalLoading: boolean;
}

export function useLoadingStateTimings(loadingStates: LoadingStates): StateTransitionTimings {
  const stateTransitionTimings = useRef<StateTransitionTimings>({
    shift: { startTime: Date.now(), endTime: undefined, duration: undefined },
    bookability: { startTime: Date.now(), endTime: undefined, duration: undefined },
    offer: { startTime: Date.now(), endTime: undefined, duration: undefined },
    blockingRequirements: { startTime: Date.now(), endTime: undefined, duration: undefined },
    hoursRestrictionConflict: { startTime: Date.now(), endTime: undefined, duration: undefined },
    shiftBoosts: { startTime: Date.now(), endTime: undefined, duration: undefined },
    arrivalTimePoints: { startTime: Date.now(), endTime: undefined, duration: undefined },
    routeEstimate: { startTime: Date.now(), endTime: undefined, duration: undefined },
    doubleBookedShift: { startTime: Date.now(), endTime: undefined, duration: undefined },
    totalLoading: { startTime: Date.now(), endTime: undefined, duration: undefined },
  });

  useTimingTracker("shift", loadingStates.shiftIsLoading, stateTransitionTimings);
  useTimingTracker("bookability", loadingStates.bookabilityIsLoading, stateTransitionTimings);
  useTimingTracker("offer", loadingStates.offerIsLoading, stateTransitionTimings);
  useTimingTracker(
    "blockingRequirements",
    loadingStates.isLoadingBlockingRequirements,
    stateTransitionTimings
  );
  useTimingTracker(
    "hoursRestrictionConflict",
    loadingStates.hasHoursRestrictionConflictIsLoading,
    stateTransitionTimings
  );
  useTimingTracker("shiftBoosts", loadingStates.shiftBoostsAreLoading, stateTransitionTimings);
  useTimingTracker(
    "arrivalTimePoints",
    loadingStates.isLoadingArrivalTimePoints,
    stateTransitionTimings
  );
  useTimingTracker(
    "routeEstimate",
    loadingStates.isRouteEstimateLoadingAndEnabled,
    stateTransitionTimings
  );
  useTimingTracker(
    "doubleBookedShift",
    loadingStates.isLoadingDoubleBookedShift,
    stateTransitionTimings
  );
  useTimingTracker("totalLoading", loadingStates.totalLoading, stateTransitionTimings);

  return stateTransitionTimings.current;
}
